import { EUserJourneyType } from '@pkg/lib-data'

import { transformProductCardsData, transformProductDetailsData } from '../transforms/main'
import type { ProductCardDataShows } from '../transforms/shows'
import type { ProductDetailsDataShows } from '../transforms/shows/details'

export interface CategoryDataResult {
  userJourneyType: EUserJourneyType | undefined
  regularList: ProductCardDataShows[]
  productsMapFiltered: Record<string, ProductDetailsDataShows>
  productsMapOriginal: Record<string, ProductDetailsDataShows>
  viewState: 'loading' | 'loaded' | 'noData' | 'hasError'
}

/**
 * Processes catalog data and returns transformed category data
 * This replaces the useCategoryData hook from React
 */
export const processCategoryData = (
  catalogsContentData: any[],
  categoryId: string,
  userJourneyType: EUserJourneyType,
): CategoryDataResult | null => {
  if (!catalogsContentData || catalogsContentData.length === 0) {
    return {
      userJourneyType,
      regularList: [],
      productsMapFiltered: {},
      productsMapOriginal: {},
      viewState: 'noData',
    }
  }

  // Transform product details
  const transformedData = transformProductDetailsData({
    catalogsContentData: catalogsContentData as any,
    userJourneyType,
    categoryId,
    getAllowedPlatforms: () => {
      return []
    }, // Widget allows all platforms
  })

  // Filter out promoted products for shows, flights, and domestic
  const allProducts = Object.values(transformedData.byIdsCatalogProducts)

  const productList = allProducts.filter((product) => {
    if (userJourneyType === EUserJourneyType.SHOWS) {
      const showsProduct = product as any

      return !showsProduct.computed.isPromoted
    }
    if (userJourneyType === EUserJourneyType.FLIGHTS) {
      const flightsProduct = product as any

      return !flightsProduct.computed?.isPromoted
    }
    if (userJourneyType === EUserJourneyType.DOMESTIC) {
      const domesticProduct = product as any

      return !domesticProduct.computed?.isPromoted
    }

    return true
  })

  // Transform to product cards
  const productsMapFiltered = productList.reduce(
    (acc, item) => {
      acc[item.computed.id] = item

      return acc
    },
    {} as Record<string, any>,
  )

  const productListPayload = transformProductCardsData({
    catalogsContentData: catalogsContentData as any,
    userJourneyType,
    categoryId,
    productsMapFiltered,
    productsMapOriginal: transformedData.byIdsCatalogProducts,
  })

  return {
    userJourneyType,
    regularList: productListPayload.default,
    productsMapFiltered,
    productsMapOriginal: transformedData.byIdsCatalogProducts,
    viewState: 'loaded',
  }
}
